import SecureStorageService from '../services/SecureStorageService';
import type { AnnotationResponse } from '../services/AnnotationService';

/** First StudyInstanceUID from a viewer URL search string. */
export function getViewerStudyUidFromSearch(search: string): string | null {
  const params = new URLSearchParams(search);
  let raw: string | null = null;
  params.forEach((value, key) => {
    if (raw == null && key.toLowerCase() === 'studyinstanceuids') {
      raw = value;
    }
  });
  if (!raw) {
    return null;
  }
  const first = raw
    .split(',')
    .map(s => s.trim())
    .find(Boolean);
  return first ?? null;
}

/** Persist grading study id to session key `id` (plain + encrypted mirror). */
export async function persistViewerStudyId(studyId: string | number | null | undefined): Promise<void> {
  const studyIdStr = studyId != null && studyId !== '' ? String(studyId) : '';
  if (!studyIdStr || typeof window === 'undefined') {
    return;
  }

  sessionStorage.setItem('id', studyIdStr);

  try {
    await SecureStorageService.getInstance().setSessionItem('id', studyIdStr);
  } catch {
    sessionStorage.setItem('id', studyIdStr);
  }
}

/** Grading DB study id from session key `id` (plain, then encrypted). */
export async function getViewerStudyIdFromSession(): Promise<string | null> {
  if (typeof window === 'undefined') {
    return null;
  }

  const fromSession = sessionStorage.getItem('id');
  if (fromSession) {
    return fromSession;
  }

  try {
    return await SecureStorageService.getInstance().getSessionItem('id');
  } catch {
    return null;
  }
}

/** Active viewer study UID: URL first, then session (plain, then encrypted). */
export async function getActiveViewerStudyUid(): Promise<string | null> {
  if (typeof window === 'undefined') {
    return null;
  }

  const fromUrl = getViewerStudyUidFromSearch(window.location.search);
  if (fromUrl) {
    return fromUrl;
  }

  const fromSession = sessionStorage.getItem('currentStudyUid');
  if (fromSession) {
    return fromSession;
  }

  try {
    return await SecureStorageService.getInstance().getSessionItem('currentStudyUid');
  } catch {
    return null;
  }
}

export function getStudyUidFromAnnotationResponse(
  response: AnnotationResponse | null | undefined
): string | null {
  const first = response?.data?.[0] as
    | { study?: { study_uid?: string }; study_info?: { study_uid?: string }; instance_uid?: string }
    | undefined;
  if (!first) {
    return null;
  }
  const study = first.study || first.study_info;
  return study?.study_uid ?? first.instance_uid ?? null;
}

/** True when global annotations payload belongs to the given study UID. */
export function isAnnotationResponseForStudyUid(
  response: AnnotationResponse | null | undefined,
  studyUid: string | null
): boolean {
  if (!studyUid) {
    return true;
  }
  const loadedUid = getStudyUidFromAnnotationResponse(response);
  if (!loadedUid) {
    return false;
  }
  return String(loadedUid) === String(studyUid);
}

/**
 * Mirrors study open persistence from StudiesList.handleRowClick:
 * isViewOnly, id (study id), currentStudyUid, lastViewedStudyInstanceUIDs, currentJobId (secure + session fallbacks).
 */
export async function applyViewerStudyOpenSession(options: {
  studyUid: string;
  studyId?: string | number | null;
  jobId: string | number;
  isViewOnly: boolean;
}): Promise<void> {
  const { studyUid, studyId, jobId, isViewOnly } = options;

  try {
    sessionStorage.setItem('isViewOnly', isViewOnly ? 'true' : 'false');
  } catch (e) {
    console.warn('[viewerStudySession] Failed to set isViewOnly:', e);
  }

  const secureStorage = SecureStorageService.getInstance();

  if (studyId != null && studyId !== '') {
    await persistViewerStudyId(studyId);
  }

  if (studyUid) {
    // Plain session mirror first so viewer init cannot race async encrypted writes.
    sessionStorage.setItem('currentStudyUid', studyUid);
    sessionStorage.setItem('lastViewedStudyInstanceUIDs', JSON.stringify([studyUid]));

    try {
      await secureStorage.setSessionItem('currentStudyUid', studyUid);
    } catch {
      sessionStorage.setItem('currentStudyUid', studyUid);
    }
    try {
      await secureStorage.setSessionItem('lastViewedStudyInstanceUIDs', JSON.stringify([studyUid]));
    } catch {
      sessionStorage.setItem('lastViewedStudyInstanceUIDs', JSON.stringify([studyUid]));
    }
  }

  const jobIdStr = jobId != null && jobId !== '' ? String(jobId) : '';
  if (jobIdStr) {
    sessionStorage.setItem('currentJobId', jobIdStr);

    try {
      await secureStorage.setSessionItem('currentJobId', jobIdStr);
    } catch {
      sessionStorage.setItem('currentJobId', jobIdStr);
    }
  }
}
